import { Button } from "@gryt/ui";
import { motion } from "motion/react";

import { usePrefersReducedMotion } from "../../lib/usePrefersReducedMotion";
import { Clip, type ClipSet } from "../Clip";
import { DownloadIcon, GlobeIcon, ServerRackIcon } from "../icons";
import { rise, stagger } from "./motion";
import styles from "./Hero.module.css";

/**
 * Twelve seconds of a Gryt server being used. Encoded with `--width 2200 --fps 60 --av1-crf
 * 30 --h264-crf 21 --duration 12`; 60fps stays because the screen share is the claim.
 */
const SELF_HOST = "https://docs.gryt.chat/docs/host/docker-compose";

const CLIENT_LIVE: ClipSet = {
  src: "/home/client-live.mp4",
  av1: "/home/client-live.av1.mp4",
  poster: "/home/client-live.poster.webp",
};

function scrollToDownload(e: React.MouseEvent) {
  e.preventDefault();
  const el = document.getElementById("download");
  if (!el) return;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduced ? "auto" : "smooth" });
}

const SHOWS =
  "A Gryt server in use: six people in a voice channel as owl tiles, one of " +
  "them sharing a screen, and the chat moving in the panel beside them";

export function Hero() {
  const prefers = usePrefersReducedMotion();
  const reduced = prefers ?? false;
  // Held until the preference is known, a render after hydration, so a reduced-motion visitor
  // gets the short fade rather than the timings the prerendered page was set up with.
  const arrive = prefers !== null;

  return (
    <section className={styles.hero}>
      <motion.div
        className={styles.inner}
        variants={stagger(reduced, 0.09)}
        initial="hidden"
        animate={arrive ? "shown" : "hidden"}
      >
        <motion.h1 className={styles.title} variants={rise(reduced)}>
          A place to talk that belongs to you.
        </motion.h1>

        <motion.p className={styles.lede} variants={rise(reduced)}>
          Gryt is voice, video and text chat. Download the app and you can make
          your own server right from it. Your chats stay on your machine, and
          nobody has to hand over an email, a phone number or a photo of their
          face to join.
        </motion.p>

        <motion.div className={styles.actions} variants={rise(reduced)}>
          <Button
            className={styles.primary}
            onClick={scrollToDownload}
            render={<a href="#download" />}
            size="large"
          >
            <DownloadIcon size={17} />
            Download Gryt
          </Button>
          {/* tone="neutral" would be a flat surface fill. The second action
              reads as glass over the page, so the tone is ghost, carrying no
              fill of its own, and the class adds only the hairline. */}
          <Button
            className={styles.secondary}
            render={<a href="https://app.gryt.chat" target="_blank" rel="noreferrer" />}
            size="large"
            tone="ghost"
          >
            <GlobeIcon size={17} />
            Try it in the browser
          </Button>
          {/* Third, and quieter than the other two on purpose. Most people are
              here to download something; the ones who want a machine of their
              own already know it, and they want the Docker page rather than a
              tour. */}
          <a className={styles.tertiary} href={SELF_HOST}>
            <ServerRackIcon size={16} />
            Self-host it
          </a>
        </motion.div>
      </motion.div>

      <motion.figure
        className={styles.shot}
        initial={{ opacity: 0, y: reduced ? 0 : 40, scale: reduced ? 1 : 0.985 }}
        animate={arrive ? { opacity: 1, y: 0, scale: 1 } : undefined}
        transition={{
          duration: reduced ? 0.15 : 0.9,
          delay: reduced ? 0 : 0.35,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        <Clip {...CLIENT_LIVE} alt={SHOWS} width={2200} height={1238} />
      </motion.figure>
    </section>
  );
}
